import React from 'react';
import { FC, ReactNode, useCallback, useRef, useState } from 'react';
import styled from 'styled-components';

interface DropdownListContainerProps {
    $orientation?: 'LEFT' | 'RIGHT';
    $verticalOrientation?: 'TOP' | 'BOTTOM';
}

const DropDownContainer = styled.div`
    border-radius: 4px;
    width: auto;
    height: 30px;
    text-align: center;
    white-space: nowrap;
    padding-left: 0.5em;
    padding-right: 0.5em;
    display: flex;
    position: relative;
    align-items: center;
    margin-left: 0.5rem;
    margin-right: 0.5rem;
`;

const DropDownHeader = styled.div`
    -webkit-user-select: none; /* Safari */
    -moz-user-select: none; /* Firefox */
    -ms-user-select: none; /* IE10+/Edge */
    user-select: none; /* Standard */
    align-self: center;
    display: flex;
`;

export const DropDownListContainer = styled.div<DropdownListContainerProps>`
    margin: 0 auto;
    z-index: 2;
    position: absolute;
    width: auto;
    ${(props) => (!props.$orientation || props.$orientation === 'LEFT') && `left: 0;`}
    ${(props) => props.$orientation && props.$orientation === 'RIGHT' && `right: 0;`}
    ${(props) => (!props.$verticalOrientation || props.$verticalOrientation === 'TOP') && `top: calc(100% + 5px);`}
    ${(props) => props.$verticalOrientation && props.$verticalOrientation === 'BOTTOM' && `bottom: calc(100% + 5px);`}
    width:auto !important;
    min-width: 10rem;
`;

export const DropDownList = styled.div`
    width: min-content !important;
    padding: 10px;
    border: 1px solid;
    border-radius: 5px;
    border-color: rgb(222, 222, 222);
    background-color: #fff;
    display: flex;
    flex-direction: column;
    box-shadow:
        rgba(0, 0, 0, 0.1) 0px 4px 6px -1px,
        rgba(0, 0, 0, 0.06) 0px 2px 4px -1px;

    & > * {
        cursor: pointer;
        -webkit-user-select: none; /* Safari */
        -moz-user-select: none; /* Firefox */
        -ms-user-select: none; /* IE10+/Edge */
        user-select: none; /* Standard */
        justify-content: left !important;
    }
`;

const ListItem = styled.div`
    list-style: none;
    width: 100%;
    display: flex;
    flex-direction: row;
    white-space: nowrap;

    > * {
        cursor: pointer;
    }
`;

const Backdrop = styled.div`
    position: fixed;
    inset: 0;
    z-index: 1;
`;

interface Props {
    header: string | ReactNode;
    items: ReactNode[];
    orientation?: 'LEFT' | 'RIGHT';
    verticalOrientation?: 'TOP' | 'BOTTOM';
}

export const SelectActions: FC<Props> = ({ header, items, orientation, verticalOrientation }) => {
    const wrapperRef = useRef(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const [isOpen, setIsOpen] = useState(false);
    const [computedVertical, setComputedVertical] = useState<'TOP' | 'BOTTOM'>(verticalOrientation ?? 'TOP');

    const toggling = useCallback(() => {
        if (!isOpen && containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            setComputedVertical(spaceBelow < 250 ? 'BOTTOM' : (verticalOrientation ?? 'TOP'));
        }
        setIsOpen(!isOpen);
    }, [isOpen, verticalOrientation]);

    return (
        <DropDownContainer
            ref={containerRef}
            onClick={(e) => {
                e.stopPropagation();
                toggling();
            }}
        >
            <DropDownHeader>{header}</DropDownHeader>
            {isOpen && (
                <>
                    <Backdrop onMouseDown={() => setIsOpen(false)} />
                    <DropDownListContainer $orientation={orientation} $verticalOrientation={computedVertical} ref={wrapperRef}>
                        <DropDownList>{items.map((item, i) => item && <ListItem key={'list_item_' + i}>{item}</ListItem>)}</DropDownList>
                    </DropDownListContainer>
                </>
            )}
        </DropDownContainer>
    );
};
